"use client";

import { useEffect, useId, useState } from "react";
import type { SbBlokData } from "@storyblok/react/rsc";
import { storyblokEditable } from "@/lib/storyblok-editable";

import { AssetImage } from "@/components/optilase/asset-image";
import { ANALYTICS_EVENTS, track } from "@/lib/analytics";
import { resolveBg } from "@/lib/bg-color";
import { isValidEmail } from "@/lib/email";
import { gtmEvent, metaLead } from "@/lib/gtm";
import { regionFromPathname, regionPath } from "@/lib/region";
import { cn } from "@/lib/utils";
import { FieldLabel, invalidField } from "@/components/ui/field-label";
import { FormGuard, useFormGuard } from "@/components/ui/form-guard";
import { Select } from "@/components/ui/select";
import { UnsavedChangesPrompt } from "@/components/ui/unsaved-changes-prompt";
import { PrivacyLink } from "@/components/ui/privacy-link";
import { usePublicPathname } from "@/lib/public-pathname";

const COUNTRIES = [
  { label: "Ireland", code: "+353" },
  { label: "United Kingdom", code: "+44" },
];

const inputClass =
  "h-12 w-full rounded-xl border border-grey-01 bg-white px-4 text-base text-black transition-colors outline-none placeholder:text-grey-04 focus:border-teal-07 focus:ring-1 focus:ring-teal-07";
const labelClass = "mb-2 block text-sm font-medium text-grey-07";

export type ContactFormBlok = SbBlokData & {
  title?: string;
  intro?: string;
  submit_label?: string;
  consent_label?: string;
  success_heading?: string;
  success_message?: string;
  image?: { filename?: string; alt?: string; focus?: string };
  image_side?: "left" | "right";
  bg_color?: string;
};

type FormState = {
  firstName: string;
  lastName: string;
  email: string;
  country: string;
  phone: string;
  preferredClinic: string;
  consent: boolean;
};

export default function ContactFormView({
  blok,
  clinics,
}: {
  blok: ContactFormBlok;
  clinics: { value: string; label: string }[];
}) {
  const pathname = usePublicPathname();
  const region = regionFromPathname(pathname);
  const privacyHref = regionPath("/privacy-policy", region);
  const { bg, onDark } = resolveBg(blok.bg_color, "white");

  const [form, setForm] = useState<FormState>({
    firstName: "",
    lastName: "",
    email: "",
    country: region === "uk" ? "United Kingdom" : "Ireland",
    phone: "",
    preferredClinic: "",
    consent: false,
  });
  const [touched, setTouched] = useState({
    firstName: false,
    lastName: false,
    email: false,
    phone: false,
  });
  const [status, setStatus] = useState<"idle" | "submitting" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [toast, setToast] = useState(false);
  const [dirty, setDirty] = useState(false);
  const guard = useFormGuard("booking");

  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(false), 6000);
    return () => clearTimeout(id);
  }, [toast]);

  const update = (field: keyof FormState, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (!dirty) {
      setDirty(true);
      track(ANALYTICS_EVENTS.BOOKING_STARTED, { region, source: "contact-page" });
    }
  };

  const fieldId = useId();
  const countryId = `${fieldId}-country`;
  const selectedCountry = COUNTRIES.find((c) => c.label === form.country);
  const phoneLength = form.country === "United Kingdom" ? 10 : 9;
  const emailValid = isValidEmail(form.email);
  const phoneComplete = form.phone.length === phoneLength;
  const firstNameError = touched.firstName && !form.firstName.trim();
  const lastNameError = touched.lastName && !form.lastName.trim();
  const emailError = touched.email && form.email.trim() !== "" && !emailValid;
  const phoneError = touched.phone && form.phone !== "" && !phoneComplete;

  const canSubmit =
    form.firstName.trim() !== "" && form.lastName.trim() !== "" && emailValid && phoneComplete;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || status === "submitting") return;
    setStatus("submitting");
    setErrorMessage(null);
    try {
      await guard.waitUntilReady();
      const res = await fetch("/api/booking/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, ...guard.payload() }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        if (typeof data?.error === "string") setErrorMessage(data.error);
        throw new Error("Submission failed");
      }
      guard.reset();
      track(ANALYTICS_EVENTS.BOOKING_SUBMITTED, { region, source: "contact-page" });
      gtmEvent("booking_submitted", { region, source: "contact-page" });
      metaLead();
      setForm((prev) => ({
        firstName: "",
        lastName: "",
        email: "",
        country: prev.country,
        phone: "",
        preferredClinic: "",
        consent: false,
      }));
      setTouched({ firstName: false, lastName: false, email: false, phone: false });
      setDirty(false);
      setStatus("idle");
      setToast(true);
    } catch {
      setStatus("error");
      track(ANALYTICS_EVENTS.FORM_SUBMISSION_FAILED, { form: "booking", region });
    }
  };

  const hasImage = Boolean(blok.image?.filename);
  const imageRight = blok.image_side === "right";

  const formColumn = (
    <>
      <div className="flex flex-col gap-4">
        {blok.title && (
          <h2 className={`text-section ${onDark ? "text-white" : "text-teal-08"}`}>{blok.title}</h2>
        )}
        {blok.intro && (
          <p className={`text-lg leading-normal ${onDark ? "text-white/80" : "text-grey-07"}`}>
            {blok.intro.trim()}
          </p>
        )}
      </div>

      <form
        onSubmit={handleSubmit}
        className="mt-8 flex flex-col gap-4 rounded-2xl bg-white p-6 lg:p-8"
      >
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <FieldLabel
              htmlFor={`${fieldId}-firstName`}
              className={labelClass}
              error={firstNameError && "Required"}
              errorId={`${fieldId}-firstName-error`}
            >
              First Name
            </FieldLabel>
            <input
              id={`${fieldId}-firstName`}
              name="firstName"
              type="text"
              required
              placeholder="First name"
              autoComplete="given-name"
              value={form.firstName}
              onChange={(e) => update("firstName", e.target.value.replace(/\d/g, ""))}
              onBlur={() => setTouched((t) => ({ ...t, firstName: true }))}
              aria-invalid={firstNameError}
              aria-describedby={`${fieldId}-firstName-error`}
              className={cn(inputClass, firstNameError && invalidField)}
            />
          </div>
          <div>
            <FieldLabel
              htmlFor={`${fieldId}-lastName`}
              className={labelClass}
              error={lastNameError && "Required"}
              errorId={`${fieldId}-lastName-error`}
            >
              Last Name
            </FieldLabel>
            <input
              id={`${fieldId}-lastName`}
              name="lastName"
              type="text"
              required
              placeholder="Last name"
              autoComplete="family-name"
              value={form.lastName}
              onChange={(e) => update("lastName", e.target.value.replace(/\d/g, ""))}
              onBlur={() => setTouched((t) => ({ ...t, lastName: true }))}
              aria-invalid={lastNameError}
              aria-describedby={`${fieldId}-lastName-error`}
              className={cn(inputClass, lastNameError && invalidField)}
            />
          </div>
        </div>

        <div>
          <FieldLabel
            htmlFor={`${fieldId}-email`}
            className={labelClass}
            error={emailError && "Enter a valid email"}
            errorId={`${fieldId}-email-error`}
          >
            Email
          </FieldLabel>
          <input
            id={`${fieldId}-email`}
            name="email"
            type="email"
            required
            placeholder="you@email.com"
            autoComplete="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, email: true }))}
            aria-invalid={emailError}
            aria-describedby={`${fieldId}-email-error`}
            className={cn(inputClass, emailError && invalidField)}
          />
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label htmlFor={countryId} className={labelClass}>
              Country
            </label>
            <Select
              id={countryId}
              value={form.country}
              onChange={(v) => update("country", v)}
              options={COUNTRIES.map((c) => ({
                value: c.label,
                label: c.label,
              }))}
            />
          </div>
          <div>
            <FieldLabel
              htmlFor={`${fieldId}-phone`}
              className={labelClass}
              error={phoneError && "Enter your full number"}
              errorId={`${fieldId}-phone-error`}
            >
              Phone
            </FieldLabel>
            <div className="flex">
              <span className="border-grey-01 text-grey-06 flex h-12 items-center rounded-l-xl border border-r-0 bg-black/3 px-3 text-base">
                {selectedCountry?.code}
              </span>
              <input
                id={`${fieldId}-phone`}
                name="phone"
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                required
                placeholder={form.country === "United Kingdom" ? "7123 456789" : "85 123 4567"}
                maxLength={phoneLength}
                value={form.phone}
                onChange={(e) =>
                  update("phone", e.target.value.replace(/\D/g, "").replace(/^0+/, ""))
                }
                onBlur={() => setTouched((t) => ({ ...t, phone: true }))}
                aria-invalid={phoneError}
                aria-describedby={`${fieldId}-phone-error`}
                className={cn(
                  "border-grey-01 placeholder:text-grey-04 focus:border-teal-07 focus:ring-teal-07 h-12 w-full rounded-r-xl border bg-white px-4 text-base text-black transition-colors outline-none focus:ring-1",
                  phoneError && invalidField,
                )}
              />
            </div>
          </div>
        </div>

        {clinics.length > 0 && (
          <div>
            <label className={labelClass}>Preferred clinic (optional)</label>
            <Select
              ariaLabel="Preferred clinic"
              name="preferredClinic"
              placeholder="No preference"
              value={form.preferredClinic}
              onChange={(v) => update("preferredClinic", v)}
              options={[{ value: "", label: "No preference" }, ...clinics]}
            />
          </div>
        )}

        <label className="flex cursor-pointer items-center gap-3">
          <input
            type="checkbox"
            name="consent"
            checked={form.consent}
            onChange={(e) => update("consent", e.target.checked)}
            className="peer sr-only"
          />
          <span
            className={`peer-focus-visible:ring-teal-07 flex size-5 shrink-0 items-center justify-center rounded-md border transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-offset-2 ${
              form.consent ? "border-teal-07 bg-teal-07" : "border-grey-02 bg-white"
            }`}
          >
            {form.consent && (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="size-3.5"
              >
                <path d="m5 12 5 5L20 7" />
              </svg>
            )}
          </span>
          <span className="text-grey-07 text-sm">
            {(blok.consent_label || "I agree to receive communications from Optilase.").trim()}
          </span>
        </label>

        <FormGuard guard={guard} />

        {status === "error" && (
          <p className="text-brand-orange-02 text-sm">
            {errorMessage ?? "Something went wrong. Please try again, or email us directly."}
          </p>
        )}

        <button
          type="submit"
          disabled={!canSubmit || status === "submitting"}
          className="bg-teal-07 hover:bg-teal-08 flex h-12 w-full cursor-pointer items-center justify-center rounded-xl font-bold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-40"
        >
          {status === "submitting"
            ? "Submitting…"
            : (blok.submit_label || "Book consultation").trim()}
        </button>

        <p className="text-grey-06 text-center text-xs">
          By submitting you agree to our <PrivacyLink href={privacyHref} />
        </p>
      </form>
    </>
  );

  return (
    <>
      <UnsavedChangesPrompt active={dirty} />
      <section {...storyblokEditable(blok)} className={bg}>
        <div className="mx-auto w-full max-w-7xl px-[clamp(1.25rem,4vw,2.5rem)] py-12 lg:py-16">
          {hasImage ? (
            <div
              className={`flex flex-col gap-10 lg:flex-row lg:items-stretch lg:gap-16 ${
                imageRight ? "lg:flex-row-reverse" : ""
              }`}
            >
              <div className="w-full lg:w-[45%] lg:shrink-0">
                <div className="relative aspect-4/5 w-full overflow-hidden rounded-3xl lg:aspect-auto lg:h-full lg:min-h-130">
                  <AssetImage
                    src={blok.image!.filename!}
                    focus={blok.image!.focus}
                    alt={blok.image!.alt || ""}
                    fill
                    sizes="(min-width: 1024px) 45vw, 100vw"
                    className="object-cover"
                  />
                </div>
              </div>
              <div className="w-full lg:flex-1">{formColumn}</div>
            </div>
          ) : (
            <div className="mx-auto w-full max-w-170">{formColumn}</div>
          )}
        </div>
      </section>

      {toast && (
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-none fixed top-24 right-4 z-100 w-[min(92vw,400px)] sm:right-8"
        >
          <div className="pointer-events-auto flex animate-[fade-in_180ms_ease-out] items-start gap-4 rounded-[20px] bg-white p-5 text-black shadow-[0_12px_28px_-12px_rgba(5,29,45,0.18)] ring-1 ring-black/5">
            <span className="bg-teal-07 mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full text-white">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="size-4"
                aria-hidden
              >
                <path d="m5 12 5 5L20 7" />
              </svg>
            </span>
            <div className="min-w-0 flex-1 pt-0.5">
              <p className="text-grey-09 text-[15px] leading-tight font-bold">
                {(blok.success_heading || "Thanks for your enquiry").trim()}
              </p>
              <p className="text-grey-06 mt-1 text-sm leading-snug">
                {(
                  blok.success_message ||
                  "We've received your details and will call you to arrange your consultation."
                ).trim()}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setToast(false)}
              aria-label="Dismiss"
              className="-mt-1 -mr-1 flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-full text-black/40 transition-colors hover:bg-black/5 hover:text-black"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.25"
                strokeLinecap="round"
                className="size-4"
                aria-hidden
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
